import React from "react";
import { useTranslation } from "react-i18next";
import { useGetUserProfileQuery } from "../../../../../api/userAuthApi";
import Skeleton from "react-loading-skeleton";
import Icon from "../../../../../../../common/components/Icon/Icon";

const OrdersTab: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { data: profile, isLoading } = useGetUserProfileQuery();

  if (isLoading) {
    return (
      <div className="orders-tab">
        <Skeleton height={200} count={3} style={{ marginBottom: "1rem" }} />
      </div>
    );
  }

  const orders = profile?.orders || [];

  if (orders.length === 0) {
    return (
      <div className="orders-tab orders-tab--empty">
        <div className="empty-state">
          <Icon iconName="shopping-bag" className="empty-state__icon" />
          <p className="empty-state__text">
            {t("user.orders.noOrders", "You haven't placed any orders yet.")}
          </p>
          <a
            href={window.commonVariables?.shopUrl || "/"}
            className="button button--primary"
          >
            {t("user.orders.shopNow", "Start Shopping")}
          </a>
        </div>
      </div>
    );
  }

  // Helper to construct the order link using the localized orderPageUrl
  // Added: origin=history parameter to differentiate view context
  const getOrderLink = (orderId: number, orderKey: string) => {
    const orderPageBaseUrl = window.commonVariables?.orderPageUrl || "/order";
    return `${orderPageBaseUrl}/${orderId}?key=${orderKey}&origin=history`;
  };

  const handleRowClick = (orderId: number, orderKey: string) => {
    window.location.href = getOrderLink(orderId, orderKey);
  };

  return (
    <div className="orders-tab">
      <h3 className="profile-section-title">
        {t("user.orders.title", "Order History")}
      </h3>
      <div className="orders-table-wrapper">
        <table className="orders-table">
          <thead>
            <tr>
              <th>{t("user.orders.table.order", "Order")}</th>
              <th>{t("user.orders.table.date", "Date")}</th>
              <th>{t("user.orders.table.status", "Status")}</th>
              <th>{t("user.orders.table.total", "Total")}</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => {
              // Pass the current language code (i18n.language) to ensure correct locale formatting
              const formattedDate = new Date(order.date).toLocaleDateString(
                i18n.language,
                {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                }
              );

              const orderUrl = getOrderLink(order.id, order.orderKey);

              return (
                <tr
                  key={order.id}
                  onClick={() => handleRowClick(order.id, order.orderKey)}
                  className="orders-table__row--clickable"
                  role="link"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleRowClick(order.id, order.orderKey);
                    }
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <td data-label={t("user.orders.table.order", "Order")}>
                    {/* Keep the anchor tag for semantic value and right-click functionality */}
                    <a
                      href={orderUrl}
                      className="order-number-link"
                      onClick={(e) => e.stopPropagation()} // Prevent double navigation if clicking link directly
                    >
                      <span className="order-number">#{order.orderNumber}</span>
                    </a>
                  </td>
                  <td data-label={t("user.orders.table.date", "Date")}>
                    {formattedDate}
                  </td>
                  <td data-label={t("user.orders.table.status", "Status")}>
                    <span
                      className={`order-status-badge order-status-badge--${order.status}`}
                    >
                      {t(`orderPage.status.${order.status}`, order.status)}
                    </span>
                  </td>
                  <td data-label={t("user.orders.table.total", "Total")}>
                    {/* Render the HTML price string from WooCommerce */}
                    <span
                      dangerouslySetInnerHTML={{ __html: order.total }}
                      style={{ fontWeight: "bold" }}
                    />
                    <span className="item-count">
                      {" "}
                      {t("user.orders.itemCount", {
                        count: order.itemCount,
                        defaultValue: "for {{count}} items",
                      })}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OrdersTab;
